import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  categories as staticCategories,
  menuItems as staticMenuItems,
  restaurantInfo,
  allergenInfo,
} from '@/data/menuData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuDataContext = createContext({
  categories: staticCategories,
  menuItems: staticMenuItems,
  loading: false,
  reload: () => {},
});

// Normalize a backend menu item doc into the shape used by the frontend components
const normalizeItem = (it) => ({
  id: it.item_id,
  name: it.name,
  description: it.description,
  price: it.price,
  image: it.image,
  allergens: it.allergens || [],
  isPopular: !!it.isPopular,
  isSignature: !!it.isSignature,
  isSpicy: !!it.isSpicy,
  isVegan: !!it.isVegan,
  isHealthy: !!it.isHealthy,
  hasVideo: !!it.hasVideo,
  video: it.video || undefined,
  objectFit: it.objectFit || undefined,
  priceText: it.priceText || undefined,
  extras: it.extras || undefined,
  forPeople: it.forPeople || undefined,
});

const normalizeCategory = (c) => ({
  id: c.category_id,
  name: c.name,
  description: c.description,
  image: c.image,
});

export const MenuDataProvider = ({ children }) => {
  const [categories, setCategories] = useState(staticCategories);
  const [menuItems, setMenuItems] = useState(staticMenuItems);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/menu-all`);
      if (!res.ok) throw new Error('API error');
      const json = await res.json();
      if (json?.categories?.length > 0) {
        setCategories(json.categories.map(normalizeCategory));
        const items = {};
        Object.keys(json.menuItems || {}).forEach((catId) => {
          items[catId] = (json.menuItems[catId] || []).map(normalizeItem);
        });
        setMenuItems(items);
      }
    } catch (e) {
      // Fallback to static data
      console.warn('[MenuData] Fallback to static data:', e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MenuDataContext.Provider
      value={{ categories, menuItems, loading, reload: load, restaurantInfo, allergenInfo }}
    >
      {children}
    </MenuDataContext.Provider>
  );
};

export const useMenuData = () => useContext(MenuDataContext);
